/**
 * Simple localStorage-based cache utility for materials management
 * 
 * Provides caching with expiration for API responses to improve performance
 * and reduce Airtable API calls, especially for program lists and session data.
 */

const CACHE_KEYS = {
  PROGRAMS_LIST: 'materials_programs_list',
  PROGRAM_SESSIONS: 'materials_program_sessions_',
  PROGRAM_DETAILS: 'materials_program_details_'
};

const DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Get data from localStorage cache
 * @param {string} key - Cache key
 * @param {number} maxAge - Maximum age in milliseconds (default: 5 minutes)
 * @returns {*|null} - Cached data or null if expired/not found
 */
export const getCachedData = (key, maxAge = DEFAULT_CACHE_DURATION) => {
  try {
    const cachedItem = localStorage.getItem(key);
    if (!cachedItem) return null;

    const { data, timestamp } = JSON.parse(cachedItem);
    const now = Date.now();
    
    if (now - timestamp > maxAge) {
      // Cache expired, remove it
      localStorage.removeItem(key);
      return null;
    }
    
    return data;
  } catch (error) {
    console.warn('Cache read error:', error);
    // If there's any error, just return null and continue without cache
    return null;
  }
};

/**
 * Store data in localStorage cache
 * @param {string} key - Cache key
 * @param {*} data - Data to cache
 */
export const setCachedData = (key, data) => {
  try {
    const cacheItem = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cacheItem));
  } catch (error) {
    console.warn('Cache write error:', error);
    // If there's any error storing to cache, just continue without caching
    // This handles cases like localStorage being full or unavailable
  }
};

/**
 * Clear specific cache key
 * @param {string} key - Cache key to clear
 */
export const clearCachedData = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Cache clear error:', error);
  }
};

/**
 * Clear all materials management cache
 */
export const clearAllMaterialsCache = () => {
  try {
    Object.values(CACHE_KEYS).forEach(keyPrefix => {
      // For keys with prefixes, we need to find all matching keys
      if (keyPrefix.endsWith('_')) {
        // Find all keys that start with this prefix
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith(keyPrefix)) {
            localStorage.removeItem(key);
          }
        }
      } else {
        localStorage.removeItem(keyPrefix);
      }
    });
  } catch (error) {
    console.warn('Cache clear all error:', error);
  }
};

/**
 * Get cache key for program sessions
 * @param {string} programId - Program ID
 * @returns {string} - Cache key
 */
export const getProgramSessionsCacheKey = (programId) => {
  return `${CACHE_KEYS.PROGRAM_SESSIONS}${programId}`;
};

/**
 * Get cache key for program details
 * @param {string} programId - Program ID
 * @returns {string} - Cache key
 */
export const getProgramDetailsCacheKey = (programId) => {
  return `${CACHE_KEYS.PROGRAM_DETAILS}${programId}`;
};

// Export cache keys for direct use
export { CACHE_KEYS, DEFAULT_CACHE_DURATION };